import React, { useState, useEffect } from "react";
import { X, Download, Share, ChevronUp } from "lucide-react";
import { Button } from "./ui/button";

// Detect iOS
const isIOS = () => {
  if (typeof window === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

// Detect if app is in standalone mode (already installed)
const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
};

// Check if prompt was dismissed recently
const wasDismissedRecently = () => {
  const dismissed = localStorage.getItem("pwa-install-dismissed");
  if (!dismissed) return false;
  const dismissedTime = parseInt(dismissed, 10);
  const sevenDays = 7 * 24 * 60 * 60 * 1000;
  return Date.now() - dismissedTime < sevenDays;
};

export default function PWAInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Don't show if already installed or dismissed recently
    if (isStandalone() || wasDismissedRecently()) {
      return;
    }

    // Handle iOS Safari (no beforeinstallprompt support)
    if (isIOS()) {
      // Show iOS prompt after a delay
      const timer = setTimeout(() => {
        setShowIOSPrompt(true);
      }, 3000);
      return () => clearTimeout(timer);
    }

    // Handle Android/Desktop Chrome
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a short delay
      setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === "accepted") {
        console.log("PWA installed");
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
    } catch (error) {
      console.error("Install error:", error);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem("pwa-install-dismissed", Date.now().toString());
    setShowPrompt(false);
    setShowIOSPrompt(false);
  };

  // Android/Desktop Install Prompt
  if (showPrompt) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 animate-in slide-in-from-bottom duration-300">
        <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
          <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5a8a] p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Download className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg">Install BQL Leads</h3>
                  <p className="text-white/80 text-sm">Add to your home screen</p>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="text-white/70 hover:text-white p-1"
                aria-label="Dismiss"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <div className="p-4">
            <p className="text-slate-600 text-sm mb-4">
              Install the BQL Leads app for faster access, offline support, and a better experience.
            </p>
            
            <div className="flex gap-3">
              <Button
                onClick={handleDismiss}
                variant="outline"
                className="flex-1"
              >
                Not Now
              </Button>
              <Button
                onClick={handleInstall}
                className="flex-1 bg-[#1e3a5f] hover:bg-[#152a45]"
              >
                <Download className="w-4 h-4 mr-2" />
                Install
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // iOS Install Instructions
  if (showIOSPrompt) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 animate-in slide-in-from-bottom duration-300">
        <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
          <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5a8a] p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Download className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg">Install BQL Leads</h3>
                  <p className="text-white/80 text-sm">Add to your home screen</p>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="text-white/70 hover:text-white p-1"
                aria-label="Dismiss"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <div className="p-4">
            <p className="text-slate-600 text-sm mb-4">
              Install BQL Leads on your iPhone for the best experience:
            </p>
            
            {/* iOS Installation Steps */}
            <div className="space-y-3 mb-4">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 bg-[#1e3a5f] text-white rounded-full flex items-center justify-center text-sm font-bold">
                  1
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-700 text-sm">Tap the</span>
                  <div className="w-7 h-7 bg-[#007AFF] rounded flex items-center justify-center">
                    <Share className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-slate-700 text-sm font-medium">Share</span>
                  <span className="text-slate-700 text-sm">button</span>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 bg-[#1e3a5f] text-white rounded-full flex items-center justify-center text-sm font-bold">
                  2
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-700 text-sm">Scroll down and tap</span>
                  <span className="text-slate-700 text-sm font-medium">"Add to Home Screen"</span>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 bg-[#1e3a5f] text-white rounded-full flex items-center justify-center text-sm font-bold">
                  3
                </div>
                <span className="text-slate-700 text-sm">Tap <span className="font-medium">"Add"</span> in the top right</span>
              </div>
            </div>
            
            {/* Arrow pointing down to Safari toolbar */}
            <div className="flex justify-center mb-3">
              <div className="animate-bounce">
                <ChevronUp className="w-6 h-6 text-[#1e3a5f] rotate-180" />
              </div>
            </div>
            
            <Button
              onClick={handleDismiss}
              variant="outline"
              className="w-full"
            >
              Got it
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
